import { CalendarDaysIcon, PencilIcon } from "@heroicons/react/24/outline";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
  RetentionOptions,
  UpdateDataRetentionPolicyDetailDto,
  CreateDataRetentionPolicyDetailDto,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  Button,
  Modal,
  SelectMenuStandard,
  SimpleCheckboxControlled,
  TextAreaBase,
  TextInputBase,
} from "@truevault/aquilo";
import { DATA_RETENTION_CATEGORIES, descriptionForTypeAndPeriod } from "./dataRetentionCategories";

export { DATA_RETENTION_CATEGORIES, descriptionForTypeAndPeriod };

type DataRetentionCategoryQuestionProps = {
  detail: DataRetentionPolicyCategoryDto | null;
  picOptions: CategoryDto[];
  onUpdate: (dto: UpdateDataRetentionPolicyDetailDto) => void;

  onRemove?: () => void;
  setApproved?: (retentionKey: string, approved: boolean) => void;
  onCreate?: (dto: CreateDataRetentionPolicyDetailDto) => void;
  onCreated?: () => void;
};

export const DataRetentionCategoryQuestion: React.FC<DataRetentionCategoryQuestionProps> = ({
  detail,
  picOptions,
  onUpdate,
  onCreate,
  onCreated,
  onRemove,
  setApproved: setDetailApproved,
}) => {
  const isAdd = detail == null;

  const [approved, setApproved] = useState(detail?.approved ?? false);
  const [hasUpdated, setHasUpdated] = useState(false);
  const [selectedPic, setSelectedPic] = useState(detail?.pic ?? []);
  const selectablePicIds = useMemo(() => new Set(picOptions.map((pic) => pic.id)), [picOptions]);
  const validSelectedPic = useMemo(
    () => selectedPic.filter((id) => selectablePicIds.has(id)),
    [selectedPic, selectablePicIds],
  );

  let startingDescription = detail?.description ?? "";
  if (startingDescription === "" && DATA_RETENTION_CATEGORIES[detail?.builtinType]?.description) {
    startingDescription = DATA_RETENTION_CATEGORIES[detail?.builtinType]?.description;
  }
  const [description, setDescription] = useState(startingDescription);
  const [lastCustom, setLastCustom] = useState(detail?.description ?? "");
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const [label, setLabel] = useState(labelForDetail(detail));
  const type = detail?.builtinType;
  const formattedLabel = formattedCategoryLabel(type, label);

  const canSetRetention =
    detail?.builtinType != null && builtinDescriptionForType(detail.builtinType).includes("YEARS");
  const defaultRetentionPeriod: RetentionOptions = canSetRetention ? "YEARS_7" : "CUSTOM";
  // Determine initial retention period:
  // - If category can't set retention (no "YEARS" placeholder in template) → "CUSTOM"
  // - If no existing detail → use default ("YEARS_7")
  // - If existing detail has null retention → "CUSTOM" (user cleared it)
  // - Otherwise → use saved retention period, falling back to default
  const initialRetentionPeriod: RetentionOptions = canSetRetention
    ? detail
      ? detail.retentionPeriod === null
        ? "CUSTOM"
        : detail.retentionPeriod ?? defaultRetentionPeriod
      : defaultRetentionPeriod
    : "CUSTOM";
  const [retentionPeriod, innerSetRetentionPeriod] =
    useState<RetentionOptions>(initialRetentionPeriod);

  // Track when retention period is changed by user so we still save even if description updates programmatically
  const retentionChangeRef = useRef(false);

  const setRetentionPeriod = (value: RetentionOptions) => {
    retentionChangeRef.current = true;
    innerSetRetentionPeriod((old) => {
      if (old !== value) {
        setApproved(false);
      }
      return value;
    });
    if (value == "CUSTOM") {
      // If we haven't already saved the retention period, and we have no previous custom description, then copy over whatever the current description is to the custom description to make it editable
      if (lastCustom == "" && detail?.retentionPeriod === null) {
        setDescription(description);
      } else {
        setDescription(lastCustom);
      }
    }
    setHasUpdated(true);
  };

  useEffect(() => {
    if (hasUpdated) {
      setApproved(false);
    }
  }, [description]);

  const [suggestedCategories, otherCategories] = useMemo(() => {
    const suggestedKeys = detail?.builtinType
      ? DATA_RETENTION_CATEGORIES[detail.builtinType].suggested
      : [];
    return _.partition(picOptions, (pic) => suggestedKeys.includes(pic.key));
  }, [detail?.builtinType, picOptions]);

  // Track if component has mounted to prevent saves on initial load
  const isMountedRef = useRef(false);
  const isProgrammaticUpdateRef = useRef(false);

  // Use refs to store latest values for the debounced save function
  const onUpdateRef = useRef(onUpdate);
  const onCreateRef = useRef(onCreate);
  const onCreatedRef = useRef(onCreated);
  const isAddRef = useRef(isAdd);
  const detailIdRef = useRef(detail?.id);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onCreateRef.current = onCreate;
    onCreatedRef.current = onCreated;
    isAddRef.current = isAdd;
    detailIdRef.current = detail?.id;
  }, [onUpdate, onCreate, onCreated, isAdd, detail?.id]);

  // Mark as mounted after first render
  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Track when description is being set programmatically (only when type or retentionPeriod changes)
  useEffect(() => {
    const newDescription = descriptionForTypeAndPeriod(type, retentionPeriod);
    if (newDescription) {
      isProgrammaticUpdateRef.current = true;
      setDescription(newDescription);
      // Reset flag after a tick to allow saves again
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, retentionPeriod]);

  // Create stable debounced save function
  const debouncedSaveRef = useRef(
    _.debounce(
      (saveData: {
        pic: string[];
        description: string;
        customLabel: string | null;
        retentionPeriod: RetentionOptions | null;
        approved: boolean;
      }) => {
        if (!isAddRef.current && detailIdRef.current) {
          onUpdateRef.current(saveData);
        }
      },
      750,
    ),
  );

  useEffect(() => () => debouncedSaveRef.current.cancel(), []);

  // Save when any relevant field changes (debounced - waits for inactivity)
  // Only save after mount and when user has made changes (not programmatic updates)
  useEffect(() => {
    const ignoreProgrammaticDueToRetentionChange = retentionChangeRef.current === true;
    if (
      !isMountedRef.current ||
      (isProgrammaticUpdateRef.current && !ignoreProgrammaticDueToRetentionChange)
    ) {
      return;
    }
    if (!hasUpdated) {
      return;
    }

    let isApproved = approved;
    const finalDescription =
      retentionPeriod === "CUSTOM"
        ? description
        : descriptionForTypeAndPeriod(type, retentionPeriod) ?? description;

    if (validSelectedPic.length === 0) {
      isApproved = false;
    } else {
      // Auto-approve when all required fields are complete
      const hasValidDescription = finalDescription && finalDescription.trim().length > 0;
      const hasValidLabel = type != null || (label && label.trim().length > 0);

      if (hasValidDescription && hasValidLabel && retentionPeriod) {
        isApproved = true;
      }
    }

    // Update local approved state if it changed
    if (isApproved !== approved) {
      setApproved(isApproved);
    }

    if (isAdd || !detail?.id) {
      return;
    }

    debouncedSaveRef.current({
      pic: validSelectedPic,
      description: finalDescription,
      customLabel: type == null ? label : null,
      retentionPeriod: retentionPeriod,
      approved: isApproved,
    });

    // Reset the retention change flag after scheduling save
    retentionChangeRef.current = false;
  }, [
    hasUpdated,
    selectedPic,
    validSelectedPic,
    description,
    retentionPeriod,
    approved,
    label,
    type,
    isAdd,
    detail?.id,
  ]);

  // Keep latest setDetailApproved in a ref to avoid effect loops due to changing identity
  const setDetailApprovedRef = useRef(setDetailApproved);
  useEffect(() => {
    setDetailApprovedRef.current = setDetailApproved;
  }, [setDetailApproved]);

  // Notify parent when local approved changes
  useEffect(() => {
    if (detail?.id && setDetailApprovedRef.current) {
      setDetailApprovedRef.current(detail.id, approved);
    }
  }, [approved, detail?.id]);

  // Track last custom description only when appropriate
  useEffect(() => {
    if (retentionPeriod == "CUSTOM" && approved && hasUpdated) {
      setLastCustom(description);
    }
  }, [retentionPeriod, approved, hasUpdated, description]);

  useEffect(() => {
    if (isAdd) {
      return; // don't auto-select suggested while adding to avoid unexpected saves/moves
    }
    if (selectedPic.length == 0 && suggestedCategories.length != 0 && !hasUpdated) {
      // auto-select suggested
      const selected = new Set<string>();
      suggestedCategories.forEach((pic) => {
        selected.add(pic.id);
      });
      setSelectedPic([...selected]);
      setHasUpdated(true);
    }
  }, [selectedPic, suggestedCategories, hasUpdated, isAdd]);

  const togglePic = (id: string, checked: boolean) => {
    setSelectedPic((old) => {
      const set = new Set(old);
      if (checked) {
        set.add(id);
      } else {
        set.delete(id);
      }
      return [...set];
    });
    setHasUpdated(true);
  };
  const controlPrefix = `retention-${detail?.id ?? "new"}`;
  const picGrid = (categories: CategoryDto[]) => (
    <div className="grid grid-cols-2 gap-x-[24px] gap-y-[12px]">
      {categories.map((cat) => (
        <SimpleCheckboxControlled
          key={cat.id}
          name={`${controlPrefix}-${cat.id}`}
          label={cat.name}
          checked={validSelectedPic.includes(cat.id)}
          onChange={(checked) => togglePic(cat.id, checked)}
        />
      ))}
    </div>
  );
  const sectionTitle = (icon: React.ReactNode, text: string) => (
    <div className="flex items-center gap-[8px] text-[14px] font-medium text-neutral-900">
      {icon}
      {text}
    </div>
  );
  const commitLabel = (value: string) => {
    setLabel(value);
    setHasUpdated(true);
  };

  // Same card as the Vendor List tables: white header row with the purpose name and the
  // "Not Applicable" control, then a 24px body of 16px/14px Tailwind controls.
  return (
    <div className="mb-[16px] bg-white border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D] overflow-hidden">
      <div className="bg-neutral-0 border-b border-solid border-neutral-200 px-[24px] py-[11px] flex items-center justify-between gap-[12px]">
        {isAdd ? (
          <div className="w-full max-w-[520px]">
            <TextInputBase
              field={`${controlPrefix}-label`}
              placeholder="Add a retention purpose, keep it short and sweet!"
              value={label ?? ""}
              onChange={(e) => commitLabel(e.target.value ?? "")}
            />
          </div>
        ) : type == null ? (
          isEditingTitle ? (
            <div className="w-full max-w-[420px]">
              <TextInputBase
                field={`${controlPrefix}-label`}
                value={label ?? ""}
                onChange={(e) => commitLabel(e.target.value ?? "")}
                onBlur={() => setIsEditingTitle(false)}
                autoFocus
              />
            </div>
          ) : (
            <button
              type="button"
              className="flex items-center gap-[8px] bg-transparent border-0 p-0 cursor-pointer text-[16px] font-medium text-neutral-900"
              onClick={() => setIsEditingTitle(true)}
              title="Click to edit title"
            >
              {label || "Untitled Retention Purpose"}
              <PencilIcon className="h-4 w-4 text-primary-600" />
            </button>
          )
        ) : (
          <div className="text-[16px] font-medium text-neutral-900">{formattedLabel}</div>
        )}
        {!isAdd && (
          <SimpleCheckboxControlled
            name={`${controlPrefix}-not-applicable`}
            label={<span className="text-[14px] font-medium text-neutral-700">Not Applicable</span>}
            checked={false}
            onChange={(checked) => {
              if (checked) {
                setRemoveDialogOpen(true);
              }
            }}
          />
        )}
      </div>
      {removeDialogOpen && (
        <Modal
          title="Mark this retention purpose as not applicable?"
          body="You will lose any collection and retention period data you previously selected."
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setRemoveDialogOpen(false)}
          secondButton="Yes, I'm sure"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            onRemove?.();
            setRemoveDialogOpen(false);
          }}
          showOverlay
          onClose={() => setRemoveDialogOpen(false)}
        />
      )}

      <div className="p-[24px] flex flex-col gap-[24px]">
        {suggestedCategories.length > 0 && (
          <div className="rounded-[8px] bg-primary-50 p-[16px]">
            {sectionTitle(
              <img
                src="/assets/images/surveys/onboarding/suggested-data-icon.svg"
                alt=""
                className="h-[20px] w-[20px]"
              />,
              "Suggested data",
            )}
            <div className="mt-[12px]">{picGrid(suggestedCategories)}</div>
          </div>
        )}

        {otherCategories.length > 0 && (
          <div>
            {sectionTitle(
              <img
                src="/assets/images/surveys/onboarding/data-collection-icon.svg"
                alt=""
                className="h-[20px] w-[20px]"
              />,
              "Other data you collect",
            )}
            <div className="mt-[12px]">{picGrid(otherCategories)}</div>
          </div>
        )}

        <div>
          {sectionTitle(
            <CalendarDaysIcon className="h-[20px] w-[20px] text-primary-600" />,
            "How long do you retain this data?",
          )}
          {type === "SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING" && (
            <div className="mt-[4px] text-[14px] leading-[20px] text-neutral-500">
              It is recommended not to retain data longer than 3 years for marketing purposes.
            </div>
          )}
          {canSetRetention && (
            <div className="mt-[12px] max-w-[300px]">
              <SelectMenuStandard
                items={[
                  { label: "2 years", value: "YEARS_2" },
                  { label: "5 years", value: "YEARS_5" },
                  { label: "7 years", value: "YEARS_7" },
                  { label: "10 years", value: "YEARS_10" },
                  { label: "Indefinite", value: "INDEFINITE" },
                  { label: "Custom", value: "CUSTOM" },
                ]}
                onSelect={(value) => setRetentionPeriod(value as RetentionOptions)}
                selectedValue={retentionPeriod}
                placeholder="Select retention period"
              />
            </div>
          )}
          {Boolean(retentionPeriod) && (
            <div className="mt-[16px]">
              {retentionPeriod == "CUSTOM" ? (
                <>
                  {canSetRetention && (
                    <>
                      <div className="text-[14px] font-medium text-neutral-900">
                        Retention period
                      </div>
                      <div className="mt-[4px] mb-[8px] text-[14px] leading-[20px] text-neutral-500">
                        Describe a retention period for data that is reasonable for the purposes for
                        which the data is used.
                      </div>
                    </>
                  )}
                  <TextAreaBase
                    field={`${controlPrefix}-description`}
                    rows={6}
                    value={description}
                    placeholder="Write your custom retention period here"
                    onChange={(e) => {
                      setHasUpdated(true);
                      setDescription(e.target.value);
                      setApproved(false);
                    }}
                  />
                  {!canSetRetention && !detail?.builtinType && (
                    <div className="mt-[8px] text-[14px] leading-[20px] text-neutral-500">
                      Example: We keep data used for [your custom purpose here] for up to [X] years.
                    </div>
                  )}
                </>
              ) : canSetRetention ? (
                <div className="rounded-[8px] border border-solid border-neutral-200 bg-neutral-0 px-[16px] py-[12px] text-[14px] leading-[20px] text-neutral-700">
                  {formattedDescription(description)}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {isAdd && (
          <div className="flex justify-end gap-[12px]">
            <Button color="secondary" size="s" onClick={() => onCreated?.()}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="s"
              disabled={!label || label.trim().length === 0}
              onClick={() => {
                if (!onCreate) return;
                onCreate({
                  builtinType: null,
                  description: description ?? "",
                  customLabel: label ?? "",
                  retentionPeriod: retentionPeriod,
                  pic: selectedPic,
                  approved: approved,
                });
                onCreated?.();
              }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const labelForDetail = (detail: DataRetentionPolicyCategoryDto | null): string => {
  if (!detail) {
    return "";
  }

  if (detail.builtinType) {
    return DATA_RETENTION_CATEGORIES[detail.builtinType].label;
  }

  return detail.customLabel ?? "";
};

export const formattedCategoryLabel = (
  type: DataRetentionPolicyDetailCategory | null | undefined,
  label: string,
): React.ReactNode => {
  if (!type) {
    return label;
  }
  const category = DATA_RETENTION_CATEGORIES[type];

  const [before, after] = category.label.split(category.strong, 2);
  return (
    <>
      <span className="text-weight-light">{before}</span>
      <span className="text-weight-medium">{category.strong}</span>
      <span className="text-weight-light">{after}</span>
    </>
  );
};

const formattedDescription = (description: string): React.ReactNode => {
  const re = /(\d+) years/i;
  const match = description.match(re);

  // If there's no "X years" pattern, just return the description as-is
  if (!match) {
    return description;
  }

  const all = description.split(re, 3);
  const [before, capture, after] = all;

  return (
    <>
      {before}
      <span className="text-primary-600 font-medium">{capture} years</span>
      {after}
    </>
  );
};

const builtinDescriptionForType = (type: DataRetentionPolicyDetailCategory): string => {
  return DATA_RETENTION_CATEGORIES[type].description;
};

export const getDefaultDescription = (detail: DataRetentionPolicyCategoryDto): string | null => {
  if (!detail.builtinType) return null;
  const template = DATA_RETENTION_CATEGORIES[detail.builtinType]?.description;
  if (!template) return null;
  return descriptionForTypeAndPeriod(detail.builtinType, detail.retentionPeriod) ?? template;
};
